import { Link } from 'react-router-dom'
import { Box, Container, Title, SimpleGrid, List, Divider, Group } from '@mantine/core'
import type { Sx } from '@mantine/core'
import {
  IoShieldCheckmarkOutline,
  IoConstructOutline,
  IoChatbubblesOutline,
  IoFlash,
  IoListOutline,
  IoHammerOutline,
  IoLockClosedOutline,
  IoNewspaperOutline,
  IoPlayCircleOutline,
  IoPeopleOutline,
  IoEyeOffOutline,
  IoScanOutline,
  IoFlagOutline,
  IoArrowForward
} from 'react-icons/io5'
import { GiAmmoBox } from 'react-icons/gi'
import { AppText, AppStack, AppCard, AppButton, AmmoScoreBadge, SiteLegalFooter, GunSlopWordmark } from '../../app/common/ui'
import bulletPointIconUrl from '../../app/icon-9mm-bulletpoint/icon-styled.svg?url'

const CROSSHAIR = {
  backgroundImage:
    'repeating-linear-gradient(0deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px), repeating-linear-gradient(90deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px)'
}

const PRINCIPLES: Array<{ title: string; body: string; icon: React.ReactNode }> = [
  {
    title: 'Community-verified information',
    body: 'Every firearm in the Registry, every part in the Parts Bin, and every build shared on GunSlop comes from the community. Users contribute, verify, and improve the data. The AmmoScore system ensures that the most reliable contributors earn standing on the platform.',
    icon: <IoShieldCheckmarkOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'Built for real use',
    body: 'We exist to solve problems that gun owners actually face. Finding accurate specifications on a specific firearm. Understanding part compatibility. Seeing how others have configured similar builds. These are the things GunSlop makes simple.',
    icon: <IoConstructOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'All firearm discussions welcome',
    body: "GunSlop is a space for firearm-related conversation. Whether you're discussing technical specifications, design choices, parts selection, regulation, or anything else related to firearms — that discussion belongs here. Stay on topic. That's the only rule.",
    icon: <IoChatbubblesOutline size={26} color="var(--gs-accent)" />
  }
]

const PROVIDES = [
  'A searchable Registry of firearms with community-verified data',
  'A Parts Bin documenting compatibility and real-world information',
  'Build documentation from owners showing complete configurations',
  'A reputation system that surfaces reliable voices',
  'Tools to share your gear safely, including serial number detection'
]

const TIERS: Array<{
  name: string
  sampleScore: number
  range: string
  color: string
  bg: string
  border: string
  unlocks: string
}> = [
  {
    name: 'Newcomer',
    sampleScore: 5,
    range: '0 – 9',
    color: '#8B949E',
    bg: 'rgba(139,148,158,0.10)',
    border: 'rgba(139,148,158,0.25)',
    unlocks: 'Browse the Registry and read all platform content'
  },
  {
    name: 'Enthusiast',
    sampleScore: 25,
    range: '10 – 49',
    color: '#58A6FF',
    bg: 'rgba(88,166,255,0.10)',
    border: 'rgba(88,166,255,0.25)',
    unlocks: 'Create posts, reviews, tips, and discussion threads'
  },
  {
    name: 'Contributor',
    sampleScore: 75,
    range: '50 – 99',
    color: '#3FB950',
    bg: 'rgba(63,185,80,0.10)',
    border: 'rgba(63,185,80,0.25)',
    unlocks: 'Edit tags on firearm Registry models'
  },
  {
    name: 'Expert',
    sampleScore: 150,
    range: '100 – 199',
    color: '#E85D04',
    bg: 'rgba(232,93,4,0.12)',
    border: 'rgba(232,93,4,0.35)',
    unlocks: 'Submit registry model edits for admin review'
  },
  {
    name: 'Curator',
    sampleScore: 350,
    range: '200 – 499',
    color: '#C9A84C',
    bg: 'rgba(201,168,76,0.12)',
    border: 'rgba(201,168,76,0.35)',
    unlocks: 'Trusted curator standing — contributions carry elevated weight'
  },
  {
    name: 'Armorer',
    sampleScore: 750,
    range: '500+',
    color: '#C9A84C',
    bg: 'linear-gradient(135deg, rgba(232,93,4,0.18), rgba(201,168,76,0.18))',
    border: 'rgba(201,168,76,0.5)',
    unlocks: 'Top-tier standing. The most trusted voice on the platform'
  }
]

const EARN_EVENTS = [
  { label: 'Part submission approved', points: '+10' },
  { label: 'Reloader recipe approved', points: '+10' },
  { label: 'Registry model approved', points: '+8' },
  { label: 'Cartridge spec approved', points: '+5' },
  { label: 'Ammo product approved', points: '+5' },
  { label: 'Post upvoted by the community', points: '+4' },
  { label: 'Part or model upvoted', points: '+3' },
  { label: 'Recipe validation submitted', points: '+3' },
  { label: 'Build or comment upvoted', points: '+2' }
]

const LOSE_EVENTS = [
  { label: 'Content removed by moderation', points: '−20' },
  { label: 'Content flagged with a warning', points: '−10' },
  { label: 'Part submission rejected', points: '−5' },
  { label: 'Reloader recipe rejected', points: '−5' },
  { label: 'Registry model rejected', points: '−3' },
  { label: 'Post downvoted', points: '−2' },
  { label: 'Cartridge spec or ammo product rejected', points: '−2' },
  { label: 'Part, model, build, or comment downvoted', points: '−1' }
]

const ARSENAL: Array<{
  label: string
  tag: string
  desc: string
  href: string
  icon: React.ReactNode
  accent: string
}> = [
  {
    label: 'Firearm Registry',
    tag: 'Intel',
    desc: 'Community-curated makes, models, and specs — the deepest reference layer on the platform.',
    href: '/registry',
    icon: <IoListOutline size={24} />,
    accent: '#3FB950'
  },
  {
    label: 'Community Builds',
    tag: 'Workbench',
    desc: 'Document every part in a build. Votes surface configurations that actually work.',
    href: '/builds',
    icon: <IoHammerOutline size={24} />,
    accent: 'var(--gs-accent)'
  },
  {
    label: 'Parts Bin',
    tag: 'Catalog',
    desc: 'Real compatibility data from owners. Submissions vetted before they go live.',
    href: '/parts-bin',
    icon: <IoConstructOutline size={24} />,
    accent: '#58A6FF'
  },
  {
    label: 'Gun Vault',
    tag: 'Personal',
    desc: 'Track what you own. Share your collection publicly or lock it down completely.',
    href: '/gun-vault',
    icon: <IoLockClosedOutline size={24} />,
    accent: '#C9A84C'
  },
  {
    label: 'Ammo & Reloading',
    tag: 'Ballistics',
    desc: 'Cartridge specs, factory loads, and handload recipes with community validation.',
    href: '/ammo',
    icon: <GiAmmoBox size={24} />,
    accent: '#E85D04'
  },
  {
    label: 'Blog & Reviews',
    tag: 'Signal',
    desc: 'Owner-verified reviews, tips, and long-form posts ranked by AmmoScore — not hype.',
    href: '/blog',
    icon: <IoNewspaperOutline size={24} />,
    accent: '#58A6FF'
  },
  {
    label: 'Slop Feed',
    tag: 'Media',
    desc: 'Short-form video and photo content from the community. Raw gear, zero algorithm slop.',
    href: '/slop-feed',
    icon: <IoPlayCircleOutline size={24} />,
    accent: 'var(--gs-accent)'
  },
  {
    label: 'Caches',
    tag: 'Squads',
    desc: 'Topic-focused community spaces for builds, calibers, brands — your crew, your rules.',
    href: '/caches',
    icon: <IoPeopleOutline size={24} />,
    accent: '#3FB950'
  }
]

const ZERO_STEPS = [
  {
    num: '01',
    title: 'Create your account',
    body: 'Free to join. Browse the Registry, read builds, and explore every public resource on day one.'
  },
  {
    num: '02',
    title: 'Load your vault',
    body: 'Add firearms you own. Set visibility per piece. Your collection, your opsec posture.'
  },
  {
    num: '03',
    title: 'Contribute & earn',
    body: 'Submit parts, write reviews, document builds. Quality work moves your AmmoScore up — unlocks follow.'
  },
  {
    num: '04',
    title: 'Rise through the tiers',
    body: 'From Newcomer to Armorer. Higher standing means more edit power and more community trust.'
  }
]

const OPSEC_PILLARS = [
  {
    title: 'Serial number detection',
    body: 'Upload flows scan for serial numbers before you publish. Flagged frames get blocked — share gear without leaking sensitive data.',
    icon: <IoScanOutline size={28} color="#E85D04" />
  },
  {
    title: 'Vault-level privacy',
    body: 'Every firearm in your Gun Vault has its own visibility. Public showcase or fully private — you control the blast radius.',
    icon: <IoEyeOffOutline size={28} color="#58A6FF" />
  },
  {
    title: 'Reputation-gated edits',
    body: 'Registry and Parts Bin changes require demonstrated standing. Bad data gets voted down; repeat offenders lose score fast.',
    icon: <IoShieldCheckmarkOutline size={28} color="#3FB950" />
  }
]

const tacticalCardSx = (accent: string): Sx => ({
  position: 'relative',
  padding: '22px 20px 24px',
  background: 'var(--gs-surface)',
  border: '1px solid var(--gs-border)',
  borderRadius: 8,
  overflow: 'hidden',
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%)`,
    opacity: 0.6
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 10,
    right: 10,
    width: 14,
    height: 14,
    borderRight: `2px solid ${accent}`,
    borderBottom: `2px solid ${accent}`,
    opacity: 0.35,
    transition: 'opacity 180ms ease'
  },
  '&:hover': {
    borderColor: accent,
    boxShadow: `0 0 28px ${accent}22, 0 8px 32px rgba(0,0,0,0.35)`,
    transform: 'translateY(-2px)',
    '&::after': { opacity: 0.85 }
  }
})

const sectionEyebrowSx = {
  fontFamily: 'Oswald, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: 3,
  textTransform: 'uppercase' as const,
  color: 'var(--gs-accent)',
  marginBottom: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6
}

export default function AboutUsPage () {
  return (
    <Box sx={{ marginTop: -8 }}>

      {/* ── Hero ── */}
      <Box
        sx={{
          width: '100%',
          ...CROSSHAIR,
          padding: '48px 20px 56px',
          borderBottom: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="sm">
          <AppStack spacing="lg" align="center">
            <Group spacing={6} sx={{ alignItems: 'baseline' }}>
              <AppText
                sx={{
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  color: 'var(--gs-muted)'
                }}
              >
                About
              </AppText>
              <GunSlopWordmark
                size="xs"
                inline
                sx={{
                  fontSize: 11,
                  letterSpacing: 2,
                  color: 'var(--gs-muted)'
                }}
              />
            </Group>
            <Title
              order={1}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(28px, 4.5vw, 40px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.15
              }}
            >
              A community platform for
              <br />
              <AppText component="span" sx={{ color: 'var(--gs-accent)' }}>
                firearm lovers
              </AppText>
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 16,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 520,
                lineHeight: 1.7
              }}
            >
              GunSlop is a community platform built for firearm enthusiasts. We provide tools to document, verify,
              and share knowledge about firearms, components, and builds. Our goal is to make that information
              accessible and reliable.
            </AppText>
          </AppStack>
        </Container>
      </Box>

      {/* ── Core principles ── */}
      <Box sx={{ padding: '48px 20px 56px' }}>
        <Container size="lg">
          <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            {PRINCIPLES.map((p) => (
              <AppCard
                key={p.title}
                sx={{
                  padding: '22px 20px',
                  background: 'var(--gs-surface)',
                  transition: 'border-color 150ms ease',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                <Box sx={{ marginBottom: 14 }}>{p.icon}</Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: 0.3,
                    color: 'var(--gs-text)',
                    marginBottom: 12,
                    lineHeight: 1.25
                  }}
                >
                  {p.title}
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.65
                  }}
                >
                  {p.body}
                </AppText>
              </AppCard>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── What we provide ── */}
      <Box
        sx={{
          padding: '48px 20px 56px',
          background: 'var(--gs-surface)',
          borderTop: '1px solid var(--gs-border)',
          borderBottom: '1px solid var(--gs-border)'
        }}
      >
        <Container size="md">
          <Title
            order={2}
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              color: 'var(--gs-muted)',
              marginBottom: 20
            }}
          >
            What we provide
          </Title>
          <List
            spacing="sm"
            size="sm"
            icon={
              <Box
                component="img"
                src={bulletPointIconUrl}
                alt=""
                aria-hidden
                sx={{
                  width: 22,
                  height: 22,
                  display: 'block',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
            }
            styles={{
              item: { alignItems: 'flex-start' },
              itemWrapper: { width: '100%' }
            }}
          >
            {PROVIDES.map((line) => (
              <List.Item key={line}>
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: 'var(--gs-text)', lineHeight: 1.55 }}>
                  {line}
                </AppText>
              </List.Item>
            ))}
          </List>
        </Container>
      </Box>

      {/* ── The Arsenal ── */}
      <Box
        sx={{
          padding: '56px 20px 64px',
          ...CROSSHAIR,
          borderTop: '1px solid var(--gs-border)',
          position: 'relative',
          overflow: 'hidden'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 680,
            height: 680,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(232,93,4,0.06) 0%, transparent 65%)',
            pointerEvents: 'none'
          }}
        />
        <Container size="lg" sx={{ position: 'relative' }}>
          <AppStack spacing={0} align="center" sx={{ marginBottom: 40 }}>
            <AppText sx={sectionEyebrowSx}>
              <IoConstructOutline size={11} />
              The Arsenal
            </AppText>
            <Title
              order={2}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(22px, 3.5vw, 34px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.15,
                marginBottom: 14
              }}
            >
              Every tool. One platform.
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 540,
                lineHeight: 1.7
              }}
            >
              GunSlop is not a forum with a sidebar. It is a full stack for documenting, verifying, and sharing
              firearm knowledge — built module by module for people who actually run the gear.
            </AppText>
          </AppStack>

          <SimpleGrid
            cols={4}
            spacing="md"
            breakpoints={[
              { maxWidth: 'lg', cols: 2 },
              { maxWidth: 'sm', cols: 1 }
            ]}
          >
            {ARSENAL.map((tool) => (
              <Box
                key={tool.label}
                component={Link}
                to={tool.href}
                sx={tacticalCardSx(tool.accent)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 6,
                      background: `${tool.accent}18`,
                      border: `1px solid ${tool.accent}44`,
                      color: tool.accent
                    }}
                  >
                    {tool.icon}
                  </Box>
                  <AppText
                    sx={{
                      fontFamily: 'Oswald, sans-serif',
                      fontWeight: 600,
                      fontSize: 9,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                      color: tool.accent,
                      opacity: 0.85
                    }}
                  >
                    {tool.tag}
                  </AppText>
                </Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: 'var(--gs-text)',
                    marginBottom: 8,
                    lineHeight: 1.2
                  }}
                >
                  {tool.label}
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 13,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.6
                  }}
                >
                  {tool.desc}
                </AppText>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── AmmoScore System ── */}
      <Box
        sx={{
          padding: '56px 20px 64px',
          ...CROSSHAIR,
          borderTop: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="lg">

          {/* Section header */}
          <AppStack spacing={0} align="center" sx={{ marginBottom: 48 }}>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: 'var(--gs-accent)',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <IoFlash size={11} />
              The AmmoScore System
            </AppText>
            <Title
              order={2}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(22px, 3.5vw, 32px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.2,
                marginBottom: 16
              }}
            >
              Reputation that reflects real contribution
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 580,
                lineHeight: 1.7
              }}
            >
              AmmoScore is GunSlop's proprietary reputation system. Every vote, submission, and moderation
              outcome on the platform contributes to a member's score. Score determines tier, and tier
              determines what a member can do — a trust structure built entirely from demonstrated
              participation, not seniority.
            </AppText>
          </AppStack>

          {/* Tier cards */}
          <SimpleGrid
            cols={3}
            spacing="md"
            breakpoints={[
              { maxWidth: 'md', cols: 2 },
              { maxWidth: 'sm', cols: 1 }
            ]}
            sx={{ marginBottom: 40 }}
          >
            {TIERS.map((tier) => (
              <Box
                key={tier.name}
                sx={{
                  padding: '18px 16px 20px',
                  background: tier.bg,
                  border: `1px solid ${tier.border}`,
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <AmmoScoreBadge tier={tier.name} score={tier.sampleScore} size="sm" />
                  <AppText
                    sx={{
                      fontFamily: 'Oswald, sans-serif',
                      fontWeight: 600,
                      fontSize: 11,
                      color: tier.color,
                      letterSpacing: 0.5,
                      opacity: 0.9,
                      background: tier.bg,
                      border: `1px solid ${tier.border}`,
                      padding: '2px 7px',
                      borderRadius: 4,
                      flexShrink: 0
                    }}
                  >
                    {tier.range}
                  </AppText>
                </Box>
                <Divider sx={{ borderColor: tier.border }} />
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 13,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.6
                  }}
                >
                  {tier.unlocks}
                </AppText>
              </Box>
            ))}
          </SimpleGrid>

          {/* Points breakdown */}
          <AppCard sx={{ padding: '28px 28px 24px' }}>
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 2.5,
                textTransform: 'uppercase',
                color: 'var(--gs-muted)',
                marginBottom: 22
              }}
            >
              How scores are calculated
            </AppText>

            <SimpleGrid
              cols={2}
              spacing={40}
              breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
            >
              {/* Earn column */}
              <Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#3FB950',
                    marginBottom: 12
                  }}
                >
                  Earn points
                </AppText>
                <AppStack spacing={0}>
                  {EARN_EVENTS.map((e) => (
                    <Box
                      key={e.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '7px 0',
                        borderBottom: '1px solid var(--gs-border)',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <AppText
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: 13,
                          color: 'var(--gs-muted)',
                          lineHeight: 1.4
                        }}
                      >
                        {e.label}
                      </AppText>
                      <AppText
                        sx={{
                          fontFamily: 'Oswald, sans-serif',
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#3FB950',
                          flexShrink: 0
                        }}
                      >
                        {e.points}
                      </AppText>
                    </Box>
                  ))}
                </AppStack>
              </Box>

              {/* Lose column */}
              <Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#F85149',
                    marginBottom: 12
                  }}
                >
                  Lose points
                </AppText>
                <AppStack spacing={0}>
                  {LOSE_EVENTS.map((e) => (
                    <Box
                      key={e.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 12,
                        padding: '7px 0',
                        borderBottom: '1px solid var(--gs-border)',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <AppText
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: 13,
                          color: 'var(--gs-muted)',
                          lineHeight: 1.4
                        }}
                      >
                        {e.label}
                      </AppText>
                      <AppText
                        sx={{
                          fontFamily: 'Oswald, sans-serif',
                          fontWeight: 700,
                          fontSize: 13,
                          color: '#F85149',
                          flexShrink: 0
                        }}
                      >
                        {e.points}
                      </AppText>
                    </Box>
                  ))}
                </AppStack>
              </Box>
            </SimpleGrid>

            <Box sx={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--gs-border)' }}>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 12,
                  color: 'var(--gs-muted)',
                  lineHeight: 1.6,
                  opacity: 0.7
                }}
              >
                Scores never go below zero. Photo votes are aesthetic signals only and do not affect AmmoScore.
                Admins may apply manual adjustments for conduct not captured by automated events.
              </AppText>
            </Box>
          </AppCard>

        </Container>
      </Box>

      {/* ── Zero to Operational ── */}
      <Box
        sx={{
          padding: '56px 20px 64px',
          background: 'var(--gs-surface)',
          borderTop: '1px solid var(--gs-border)',
          borderBottom: '1px solid var(--gs-border)'
        }}
      >
        <Container size="lg">
          <SimpleGrid cols={2} spacing={48} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            <Box>
              <AppText sx={{ ...sectionEyebrowSx, color: 'var(--gs-muted)' }}>
                Zero to operational
              </AppText>
              <Title
                order={2}
                sx={{
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(24px, 3vw, 36px)',
                  color: 'var(--gs-text)',
                  lineHeight: 1.1,
                  marginBottom: 16
                }}
              >
                Four moves.
                <br />
                <AppText component="span" sx={{ color: 'var(--gs-accent)' }}>Full clearance.</AppText>
              </Title>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 15,
                  color: 'var(--gs-muted)',
                  lineHeight: 1.7,
                  maxWidth: 400
                }}
              >
                You do not need to understand every module on day one. Follow the path — browse first,
                contribute second, earn standing third.
              </AppText>
            </Box>

            <AppStack spacing={0}>
              {ZERO_STEPS.map((step, i) => (
                <Box
                  key={step.num}
                  sx={{
                    display: 'flex',
                    gap: 20,
                    padding: '20px 0',
                    borderBottom: i < ZERO_STEPS.length - 1 ? '1px solid var(--gs-border)' : undefined,
                    position: 'relative'
                  }}
                >
                  <Box sx={{ position: 'relative', flexShrink: 0, width: 52 }}>
                    <AppText
                      sx={{
                        fontFamily: 'Oswald, sans-serif',
                        fontWeight: 700,
                        fontSize: 36,
                        lineHeight: 1,
                        color: 'var(--gs-accent)',
                        opacity: 0.25,
                        position: 'absolute',
                        top: -4,
                        left: 0
                      }}
                    >
                      {step.num}
                    </AppText>
                    <AppText
                      sx={{
                        fontFamily: 'Oswald, sans-serif',
                        fontWeight: 700,
                        fontSize: 18,
                        lineHeight: 1,
                        color: 'var(--gs-accent)',
                        position: 'relative',
                        paddingTop: 4
                      }}
                    >
                      {step.num}
                    </AppText>
                  </Box>
                  <Box>
                    <AppText
                      sx={{
                        fontFamily: 'Oswald, sans-serif',
                        fontWeight: 600,
                        fontSize: 15,
                        letterSpacing: 0.3,
                        color: 'var(--gs-text)',
                        marginBottom: 6
                      }}
                    >
                      {step.title}
                    </AppText>
                    <AppText
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: 13,
                        color: 'var(--gs-muted)',
                        lineHeight: 1.65
                      }}
                    >
                      {step.body}
                    </AppText>
                  </Box>
                </Box>
              ))}
            </AppStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Built for Opsec ── */}
      <Box
        sx={{
          padding: '56px 20px 64px',
          ...CROSSHAIR,
          borderBottom: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="lg">
          <AppStack spacing={0} align="center" sx={{ marginBottom: 40 }}>
            <AppText sx={sectionEyebrowSx}>
              <IoShieldCheckmarkOutline size={11} />
              Built for opsec
            </AppText>
            <Title
              order={2}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(22px, 3.5vw, 32px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.2,
                marginBottom: 14
              }}
            >
              Share hard. Leak nothing.
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 560,
                lineHeight: 1.7
              }}
            >
              Enthusiasts want to show their setups. They also need to protect serial numbers, home addresses,
              and private collections. GunSlop bakes that tension into the product — not bolted on after the fact.
            </AppText>
          </AppStack>

          <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            {OPSEC_PILLARS.map((pillar) => (
              <Box
                key={pillar.title}
                sx={{
                  padding: '28px 24px',
                  background: 'linear-gradient(160deg, var(--gs-surface) 0%, rgba(13,17,23,0.6) 100%)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 3,
                    height: '100%',
                    background: 'linear-gradient(180deg, var(--gs-accent), transparent)'
                  }
                }}
              >
                <Box sx={{ marginBottom: 18 }}>{pillar.icon}</Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: 0.3,
                    color: 'var(--gs-text)',
                    marginBottom: 10,
                    lineHeight: 1.25
                  }}
                >
                  {pillar.title}
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.65
                  }}
                >
                  {pillar.body}
                </AppText>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── The Standard ── */}
      <Box
        sx={{
          padding: '48px 20px',
          background: 'var(--gs-surface)',
          borderBottom: '1px solid var(--gs-border)'
        }}
      >
        <Container size="md">
          <Box
            component={Link}
            to="/guidelines"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              padding: '28px 32px',
              background: 'var(--gs-bg)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8,
              textDecoration: 'none',
              color: 'inherit',
              flexWrap: 'wrap',
              transition: 'border-color 180ms ease, box-shadow 180ms ease',
              '&:hover': {
                borderColor: 'var(--gs-accent)',
                boxShadow: '0 0 24px rgba(232,93,4,0.12)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 18, flex: 1, minWidth: 240 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  background: 'rgba(232,93,4,0.12)',
                  border: '1px solid rgba(232,93,4,0.35)'
                }}
              >
                <IoFlagOutline size={24} color="var(--gs-accent)" />
              </Box>
              <Box>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: 2.5,
                    textTransform: 'uppercase',
                    color: 'var(--gs-muted)',
                    marginBottom: 8
                  }}
                >
                  The standard
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 18,
                    color: 'var(--gs-text)',
                    marginBottom: 6,
                    lineHeight: 1.2
                  }}
                >
                  Firearm talk stays on mission
                </AppText>
                <AppText
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.6,
                    maxWidth: 480
                  }}
                >
                  Specs, builds, parts, regulation, culture — if it is about firearms, it belongs here.
                  Read the community guidelines. Moderation is active. AmmoScore enforces the rest.
                </AppText>
              </Box>
            </Box>
            <Group spacing={6} sx={{ flexShrink: 0, color: 'var(--gs-accent)' }}>
              <AppText
                sx={{
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase'
                }}
              >
                Read guidelines
              </AppText>
              <IoArrowForward size={16} />
            </Group>
          </Box>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box
        sx={{
          padding: '72px 20px 80px',
          ...CROSSHAIR,
          position: 'relative',
          overflow: 'hidden'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(232,93,4,0.04) 0%, transparent 40%, rgba(232,93,4,0.03) 100%)',
            pointerEvents: 'none'
          }}
        />
        <Container size="sm" sx={{ position: 'relative' }}>
          <AppStack spacing="lg" align="center">
            <AppText
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: 'var(--gs-muted)'
              }}
            >
              Free to join
            </AppText>
            <Title
              order={2}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(26px, 4vw, 38px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.12
              }}
            >
              Lock in. Load up.
              <br />
              <AppText component="span" sx={{ color: 'var(--gs-accent)' }}>
                Start contributing.
              </AppText>
            </Title>
            <AppText
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 440,
                lineHeight: 1.7
              }}
            >
              One account gets you the Registry, Builds, Parts Bin, Vault, Ammo, Slop, and a reputation score
              that actually means something.
            </AppText>
            <Group spacing="sm" position="center">
              <AppButton
                component={Link}
                to="/register"
                size="md"
                sx={{
                  background: 'var(--gs-accent)',
                  color: '#fff',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600,
                  padding: '0 28px',
                  boxShadow: '0 4px 20px rgba(232,93,4,0.35)',
                  '&:hover': { background: '#bf4a00', boxShadow: '0 6px 28px rgba(232,93,4,0.45)' }
                }}
              >
                Create free account
              </AppButton>
              <AppButton
                component={Link}
                to="/registry"
                variant="outline"
                size="md"
                sx={{
                  borderColor: 'var(--gs-border)',
                  color: 'var(--gs-text)',
                  fontFamily: 'DM Sans, sans-serif',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                Browse the Registry
              </AppButton>
            </Group>
          </AppStack>
        </Container>
      </Box>

      <SiteLegalFooter />
    </Box>
  )
}
